import { PERSIST_STORE } from "@Constants/app";
import { create } from "zustand";
import { persist } from "zustand/middleware";


type LoginResponse = {
  isVerified:true;
  refreshToken:string;
  chatToken: string;
  refreshTokenExpiryTime:string;
  token:string;
  };

type StoreState = {
  userData: null | any;
  accessToken: null | string;
  refreshToken: null | string;
  chatToken: null | string;
  isAuth: boolean;
  hasHydrated: boolean;
  isDark: boolean;
  showSidebar: boolean; 
};

type StoreAction = {
  setUserAuthentication: (data: LoginResponse) => void;
  removeUserAuthentication: () => void;
  updateUserData: (data: any) => void;
  mergeUserData: (data: any) => void;
  markHydrated: (value: boolean) => void;
  toggleSidebar: () => void;
}; 

const normalizeAuthUserData = (payload: any, previousUserData: any) => {
  const nestedUserData = payload?.userData ?? payload?.user;

  if (nestedUserData) {
    return nestedUserData;
  }

  const topLevelUserData = {
    id: payload?.id,
    userName: payload?.userName,
    firstName: payload?.firstName,
    lastName: payload?.lastName,
    email: payload?.email,
    isActive: payload?.isActive,
    emailConfirmed: payload?.emailConfirmed,
    phoneNumber: payload?.phoneNumber,
    imageUrl: payload?.imageUrl,
    status: payload?.status,
    profileStatus: payload?.profileStatus,
    isBiometricEnabled: payload?.isBiometricEnabled,
  };

  const hasTopLevelUserData = Object.values(topLevelUserData).some(
    (value) => value !== undefined
  );

  if (hasTopLevelUserData) {
    return topLevelUserData;
  }

  return previousUserData;
};

const initialState: StoreState = {
  isAuth: false,
  hasHydrated: false,
  userData: null,
  accessToken: null,
  chatToken: null,
  refreshToken: null,
  isDark: false,
  showSidebar: false,
};

const useAuthStore = create<StoreState & StoreAction>()(
  persist(
    (set) => ({
      ...initialState,
      setUserAuthentication: (payload: LoginResponse) =>
        set((state) => ({
          isAuth: true,
          accessToken: payload?.token,
          chatToken: payload?.chatToken,
          refreshToken: payload?.refreshToken,
          userData: normalizeAuthUserData(payload, state.userData),
        })),
      removeUserAuthentication: () =>
        set(() => ({
          ...initialState,
          hasHydrated: true,
        })),
      updateUserData: (payload: any) => set(() => ({ userData: payload })),
      mergeUserData: (payload: any) =>
        set((state) => ({
          userData: {
            ...(state.userData ?? {}),
            ...(payload ?? {}),
          },
        })),
      markHydrated: (value: boolean) => set(() => ({ hasHydrated: value })),
      toggleSidebar: () =>
        set((state) => ({ showSidebar: !state.showSidebar })),
    }),
    {
      name: PERSIST_STORE,
      partialize: ({
        isAuth,
        accessToken,
        chatToken,
        refreshToken,
        userData,
        isDark,
        showSidebar,
      }) => ({
        isAuth,
        userData,
        accessToken,
        chatToken,
        refreshToken,
        isDark,
        showSidebar, 
      }),
      onRehydrateStorage: () => (state) => {
        state?.markHydrated(true);
      },
    }
  )
);

export default useAuthStore;